import React, { useState } from "react";
import { ArrowUpRight, Search, Truck } from "lucide-react";
import axios from "axios";
import Header from "@/components/Header";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_COPY = {
  awaiting_payment: { label: "Awaiting payment", tone: "warn" },
  paid: { label: "Paid · queued for production", tone: "ok" },
  fulfilled: { label: "Shipped", tone: "ok" },
  delivered: { label: "Delivered", tone: "ok" },
  cancelled: { label: "Cancelled", tone: "err" },
  expired: { label: "Expired", tone: "err" },
};

function Pill({ tone = "ok", children }) {
  const map = {
    ok: "bg-[var(--st-mustard)] text-[var(--st-fg)]",
    warn: "bg-[var(--st-orange)] text-[var(--st-fg)]",
    err: "bg-[var(--st-maroon)] text-white",
  };
  return (
    <span
      className={`inline-flex items-center px-2.5 py-1 border-2 border-[#111] font-heading uppercase tracking-[0.18em] text-[10px] ${map[tone]}`}
    >
      {children}
    </span>
  );
}

export default function Track() {
  const [orderNumber, setOrderNumber] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [order, setOrder] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setOrder(null);
    try {
      const { data } = await axios.post(`${API}/orders/lookup`, {
        order_number: orderNumber,
        email,
      });
      setOrder(data);
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not find that order.");
    } finally {
      setLoading(false);
    }
  };

  const statusMeta = order ? STATUS_COPY[order.status] || { label: order.status, tone: "ok" } : null;
  const easy = order?.easyship || {};

  return (
    <div className="min-h-screen bg-[var(--st-bg)]" data-testid="track-page">
      <Header />

      <main className="max-w-[1100px] mx-auto px-4 md:px-6 py-10">
        <h1 className="font-display uppercase tracking-tight text-5xl md:text-6xl text-[var(--st-maroon)] leading-none">
          Track Your Order
        </h1>
        <p className="mt-3 font-body text-base md:text-lg text-[var(--st-fg)]/80 max-w-2xl">
          Enter the order number from your confirmation email and the email you used at checkout.
        </p>

        <form
          onSubmit={submit}
          data-testid="track-form"
          className="mt-8 grid grid-cols-1 md:grid-cols-[1fr_1fr_auto] gap-3 max-w-3xl"
        >
          <input
            data-testid="track-order-number"
            value={orderNumber}
            onChange={(e) => setOrderNumber(e.target.value)}
            placeholder="Order # (e.g. ST-A1B2C3D4)"
            required
            className="border-2 border-[#111] bg-[var(--st-cream)] px-4 py-3 font-body text-sm focus:outline-none focus:bg-white"
          />
          <input
            data-testid="track-email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Email used at checkout"
            type="email"
            required
            className="border-2 border-[#111] bg-[var(--st-cream)] px-4 py-3 font-body text-sm focus:outline-none focus:bg-white"
          />
          <button
            data-testid="track-submit"
            type="submit"
            disabled={loading}
            className="inline-flex items-center justify-center gap-2 bg-[var(--st-maroon)] text-white border-2 border-[#111] px-5 py-3 font-heading uppercase tracking-[0.18em] text-xs hover:bg-[var(--st-orange)] hover:text-[var(--st-fg)] transition-colors shadow-brutal-sm disabled:opacity-60"
          >
            <Search size={14} /> {loading ? "Looking…" : "Look up"}
          </button>
        </form>

        {error && (
          <p data-testid="track-error" className="mt-4 font-body text-sm text-[var(--st-maroon)]">
            {error}
          </p>
        )}

        {order && (
          <section
            data-testid="track-result"
            className="mt-10 border-2 border-[#111] bg-[var(--st-cream)] shadow-brutal-sm p-5 md:p-7"
          >
            <div className="flex flex-wrap items-center gap-3">
              <p className="font-display uppercase tracking-tight text-2xl text-[var(--st-maroon)]">
                {order.order_number}
              </p>
              {statusMeta && <Pill tone={statusMeta.tone}>{statusMeta.label}</Pill>}
            </div>

            <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <p className="font-heading uppercase tracking-[0.18em] text-[10px] text-[var(--st-fg)]/60">
                  Items
                </p>
                <ul className="mt-2 flex flex-col gap-1">
                  {(order.items || []).map((it, i) => (
                    <li key={i} className="font-body text-sm text-[var(--st-fg)]">
                      {it.name} <span className="text-[var(--st-fg)]/60">× {it.quantity}</span>
                    </li>
                  ))}
                </ul>
                <p className="mt-4 font-body text-sm text-[var(--st-fg)]">
                  Total:{" "}
                  <span className="font-price text-xl text-[var(--st-maroon)] align-middle">
                    ${Number(order.total || 0).toFixed(2)}
                  </span>
                </p>
              </div>

              <div>
                <p className="font-heading uppercase tracking-[0.18em] text-[10px] text-[var(--st-fg)]/60">
                  Shipment
                </p>
                {easy.tracking_url || easy.tracking_number ? (
                  <>
                    <p className="mt-2 font-body text-sm">
                      Status:{" "}
                      <span className="font-display uppercase">{easy.status || "in transit"}</span>
                    </p>
                    {easy.tracking_number && (
                      <p className="font-body text-sm">Tracking #: {easy.tracking_number}</p>
                    )}
                    {easy.tracking_url && (
                      <a
                        href={easy.tracking_url}
                        target="_blank"
                        rel="noreferrer"
                        data-testid="track-courier-link"
                        className="mt-3 inline-flex items-center gap-2 bg-[var(--st-mustard)] text-[var(--st-fg)] px-4 py-2 border-2 border-[#111] font-heading uppercase tracking-[0.18em] text-xs hover:bg-[var(--st-orange)] transition-colors shadow-brutal-sm"
                      >
                        <Truck size={14} /> Open courier tracker <ArrowUpRight size={12} />
                      </a>
                    )}
                  </>
                ) : (
                  <p className="mt-2 font-body text-sm text-[var(--st-fg)]/70">
                    {order.status === "paid"
                      ? "Payment received. Your shipment label will be generated within 2–4 business days."
                      : "No shipment created yet."}
                  </p>
                )}
              </div>
            </div>
          </section>
        )}

        <p className="mt-10 font-body text-sm text-[var(--st-fg)]/70">
          Lost your order number? Email{" "}
          <a
            href="mailto:orders@wallofentertainment.com"
            className="underline decoration-[var(--st-mustard)] hover:text-[var(--st-maroon)]"
          >
            orders@wallofentertainment.com
          </a>{" "}
          from the address you used at checkout.
        </p>
      </main>
    </div>
  );
}
